import { graphql, PageProps } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { LayoutProvider } from '@application/Contexts/LayoutContext'
import { PageBuilder } from '@application/Interfaces/Features/PageBuilder/PageBuilder'
import { BreadcrumbPropsType, PageNodeType, SingleTypeComponentType } from '@application/Types/ComponentTypes'
import { Locale } from '@application/Types/Locale'
import { Nullable } from '@application/Types/Opt'
import { StrapiTranslations, Translations } from '@application/Types/TranslationTypes'
import { getPageUrl } from '@application/Utils/generalUtils'
import { STRAPI_COMPONENT_TYPES } from '@core/Constants/PageConstants'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { Context } from '@infrastructure/Context'
import { isGdprCookieAccepted } from '@infrastructure/Cookies'
import { GrowthBookFeaturesType } from '@infrastructure/Growthbook'
import { growthbookTrackingCallback } from '@infrastructure/Growthbook/utils'
import { localizeUrl } from '@infrastructure/Url/localizeUrls/localizeUtilities'
import { FREIGHT_GUIDE_TRANSLATION, HOME_TRANSLATION } from '@src/Core/Constants/TranslationConstants'
import { GlobalAppStyles } from '@src/GlobalAppStyles'
import { OrganizationProvider } from '@src/Infrastructure/Context/OrganizationContext'
import { findBreadcrumbIndex, getTranslations, modifiedHeroComponents } from '@src/templates/utils'
export { Head } from '@src/Application/Interfaces/Features/Seo'
const growthbook = new GrowthBook<GrowthBookFeaturesType>({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: `${process.env.GATSBY_GROWTHBOOK_CLIENT_KEY}`,
    subscribeToChanges: true,
    // Only required for A/B testing
    // Called every time a user is put into an experiment
    trackingCallback: growthbookTrackingCallback,
})

export type GlobalFreightGuideCollectionNodeType = {
    id: string
    locale: Locale
    pageUrl: string
    legacyPageUrl: Nullable<string>
    useLegacyPageUrl: boolean
    pageComponents: {
        strapi_component: string
    }[]
}

const Template = (
    props: PageProps<
        { strapiPage: PageNodeType; strapiTranslation: StrapiTranslations },
        {
            language: Locale
            globalFreightGuideCollectionNode?: GlobalFreightGuideCollectionNodeType
            singleTypeComponents: SingleTypeComponentType
        }
    >
): JSX.Element => {
    const [sendMixpanelEvents, setSendMixpanelEvents] = useState(isGdprCookieAccepted('mixpanel'))
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    })

    const strapiTranslation = props.data.strapiTranslation

    const translations: Translations = getTranslations(strapiTranslation)

    const pageComponents = props.data.strapiPage.pageComponents ?? []
    const collectionNode = props.pageContext.globalFreightGuideCollectionNode
    const pageTitle = props.data.strapiPage.title
    const language = props.pageContext.language
    const homeTitle = translations[language]?.['home'] || HOME_TRANSLATION
    const collectionTitle = translations[language]?.['frightGuide'] || FREIGHT_GUIDE_TRANSLATION

    const breadcrumbComponent: BreadcrumbPropsType = {
        id: 'breadcrumb',
        strapi_component: STRAPI_COMPONENT_TYPES.BREADCRUMB,
        items: [
            { title: homeTitle, link: '/' },
            {
                title: collectionTitle,
                link: collectionNode ? localizeUrl(language, getPageUrl(collectionNode)) : '',
            },
            { title: pageTitle || '' },
        ],
    }

    const breadcrumbIndex = findBreadcrumbIndex(pageComponents)
    if (pageComponents[breadcrumbIndex]?.strapi_component !== STRAPI_COMPONENT_TYPES.BREADCRUMB) {
        pageComponents.splice(breadcrumbIndex, 0, breadcrumbComponent)
    }

    const singleTypeUSP = props.pageContext.singleTypeComponents['usp'][language]
    const singleTypeAgilitySection = props.pageContext.singleTypeComponents['agilitySection'][language]

    const modifiedPageComponents = modifiedHeroComponents({
        pageComponents,
        singleTypeUSP,
        singleTypeAgilitySection,
    })

    useEffect(() => {
        // Load features asynchronously when the app renders
        growthbook.loadFeatures()
    }, [])

    return (
        <Context.Provider
            value={{
                locale: language,
                translations,
                sendMixpanelEvents,
                setSendMixpanelEvents,
            }}
        >
            <OrganizationProvider>
                <QueryClientProvider client={queryClient}>
                    <LayoutProvider>
                        <GrowthBookProvider growthbook={growthbook}>
                            <GlobalAppStyles />
                            <PageBuilder
                                {...{
                                    ...props,
                                    data: {
                                        ...props.data,
                                        strapiPage: {
                                            ...props.data.strapiPage,
                                            pageComponents: modifiedPageComponents,
                                        },
                                    },
                                }}
                            />
                        </GrowthBookProvider>
                    </LayoutProvider>
                </QueryClientProvider>
            </OrganizationProvider>
        </Context.Provider>
    )
}

export default Template

export const query = graphql`
    query ($id: String!) {
        strapiPage: strapiGlobalFreight(id: { eq: $id }) {
            ...StrapiGlobalFreightData
        }
        strapiTranslation {
            ...StrapiTranslations
        }
    }
`
